import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'cuantrader_install_dismissed';

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as any).standalone === true
  );
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !(window as any).MSStream;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS never fires beforeinstallprompt → show manual hint after a short delay
    let t: any;
    if (isIOS()) t = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-sm z-[60] bg-[#0a1122] border border-cyan-400/30 rounded-2xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center shrink-0">
        <Download className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">Install Aplikasi Cuantrader</p>
        {iosHint ? (
          <p className="text-xs text-white/60 mt-1 leading-relaxed">
            Ketuk <Share className="inline w-3.5 h-3.5 -mt-0.5" strokeWidth={2} /> <span className="text-white/80">Bagikan</span> lalu pilih
            <span className="text-cyan-300"> "Tambahkan ke Layar Utama"</span>.
          </p>
        ) : (
          <p className="text-xs text-white/60 mt-1">Pasang ke layar utama HP Anda agar bisa dibuka seperti aplikasi.</p>
        )}
        {!iosHint && (
          <button
            data-testid="install-prompt-accept"
            onClick={install}
            className="mt-3 bg-cyan-400 text-[#050914] font-medium px-4 py-2 rounded-lg text-sm transition-colors hover:bg-cyan-300"
          >
            Install Sekarang
          </button>
        )}
      </div>
      <button data-testid="install-prompt-dismiss" onClick={dismiss} className="text-white/40 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors shrink-0" aria-label="Tutup">
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
